// ─────────────────────────────────────────────────────────────────────────────
// VALID APP URLs
// ─────────────────────────────────────────────────────────────────────────────

import {
  CreateProjectStatus,
  GenerateProjectStatus,
  ExecuteProjectStatus,
  EnhanceProjectStatus,
  IqeProjectStatus,
} from './interfaces/ui';
import { ProjectMappingType } from './interfaces/ui/addProjectModal';
import {
  CreateLoginApiStatus,
  CreateLoginRerunApiStatus,
  CreateNoLoginApiStatus,
  CreateNoLoginRerunApiStatus,
  ExecuteApiStatus,
  ExecuteRerunApiStatus,
  EnhanceApiStatus,
  EnhanceRerunApiStatus,
  GenerateWithTestPlanApiStatus,
  GenerateWithTestPlanRerunApiStatus,
  GenerateWithoutTestPlanApiStatus,
  GenerateWithoutTestPlanRerunApiStatus,
  IqeApiStatus,
} from './interfaces/api/responses/shared.response';

import type { TestExecutionActionKey } from './utils/projectExecutionLifecycle';

export const VALID_APP_URLS = [
  'https://uat.devx.cbre.com/',
  'https://staging.devx.cbre.com/',
  'https://prod.devx.cbre.com/',
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// FORM OPTIONS
// ─────────────────────────────────────────────────────────────────────────────

export const ENVIRONMENT_OPTIONS = [
  'Dev',
  'SIT',
  'QA',
  'UAT',
  'Non-Prod',
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// FORM LABELS & MESSAGES
// ─────────────────────────────────────────────────────────────────────────────

export const PROJECT_MAPPING_OPTIONS: readonly ProjectMappingType[] = [
  'ADO',
  'JIRA',
];

export const ENHANCEMENT_TEST_CASES_LABEL =
  'Include additional test cases from ADO or JIRA';

export const DEFAULT_ENHANCEMENT_TEST_PLAN_SELECTION = {
  projectMappingType: '' as ProjectMappingType,
  mappedProjectId: '',
  mappedProjectName: '',
  mappedProjectOrg: '',
  testPlanId: '',
  testPlanName: '',
  testSuiteId: '',
  testSuiteName: '',
};

export const REPOSITORY_CONFIG_OPTIONS = ['ADO', 'GitHub'] as const;

export const VALIDATION_MESSAGES = {
  required: (fieldName: string) => `${fieldName} is required`,
  invalidUrl: 'Enter a valid URL',
  selectOption: (fieldName: string) => `Select ${fieldName}`,
  acknowledgement: 'You must acknowledge the terms to proceed',
} as const;

export const HELPER_MESSAGES = {
  create: 'Complete all required fields to create a project',
  edit: 'Complete all required fields to update a project',
} as const;

export const ACKNOWLEDGEMENT_TEXT =
  'By proceeding, you authorize the iQE Engine (Service Account: us_svc_DevX_Reg@cbre.com) to access your application using the credentials provided. This enables automated generation, execution, and reporting of test scenarios on your behalf.';

export const ACKNOWLEDGEMENT_LABEL = 'I acknowledge and agree to proceed';

export const FRONTEND_REPOSITORY_INFOTEXT =
  'Please select your frontend repo. This will be used as a context by the agent';
export const PLAYWRIGHT_REPOSITORY_INFOTEXT =
  'Provide the repository URL containing Playwright tests.';
export const FRONTEND_REPOSITORY_BRANCH_INFOTEXT = 'Default: master/main';

export const DEFAULT_FORM_DATA = {
  projectName: '',
  appUrl: '',
  loginNotRequired: false,
  environment: '',
  projectMappingType: '' as ProjectMappingType,
  mappedProjectId: '',
  mappedProjectName: '',
  mappedProjectOrg: '',
  testPlanId: '',
  testPlanName: '',
  testSuiteId: '',
  testSuiteName: '',
  repositoryConfig: '',
  frontendRepositoryName: '',
  frontendRepositoryUrl: '',
  frontendRepositoryBranch: '',
  playwrightTestRepositoryUrl: '',
  existingTestPlanId: '',
  existingTestPlanName: '',
  existingTestSuiteId: '',
  existingTestSuiteName: '',
  flowId: '',
  flowName: '',
};
export const CREATE_LOGIN_STATUS_MAP: Record<
  CreateLoginApiStatus,
  CreateProjectStatus
> = {
  CREATE_LOGIN_TRIGGERED: 'SUBMITTED',
  CREATE_LOGIN_INITIATED: 'SUBMITTED',
  CREATE_LOGIN_INPROGRESS: 'PROCESSING',
  CREATE_LOGIN_SUCCESSFUL: 'SUCCESSFUL',
  CREATE_LOGIN_FAILED: 'FAILED',
  CREATE_LOGIN_FAILED_LOGIN: 'FAILED_LOGIN',
};

export const CREATE_LOGIN_RERUN_STATUS_MAP: Record<
  CreateLoginRerunApiStatus,
  CreateProjectStatus
> = {
  CREATE_LOGIN_RERUN_TRIGGERED: 'SUBMITTED',
  CREATE_LOGIN_RERUN_INITIATED: 'SUBMITTED',
  CREATE_LOGIN_RERUN_INPROGRESS: 'PROCESSING',
  CREATE_LOGIN_RERUN_SUCCESSFUL: 'SUCCESSFUL',
  CREATE_LOGIN_RERUN_FAILED: 'FAILED',
  CREATE_LOGIN_RERUN_FAILED_LOGIN: 'FAILED_LOGIN',
};

export const CREATE_NOLOGIN_STATUS_MAP: Record<
  CreateNoLoginApiStatus,
  CreateProjectStatus
> = {
  CREATE_NOLOGIN_TRIGGERED: 'SUBMITTED',
  CREATE_NOLOGIN_INITIATED: 'SUBMITTED',
  CREATE_NOLOGIN_INPROGRESS: 'PROCESSING',
  CREATE_NOLOGIN_SUCCESSFUL: 'SUCCESSFUL',
  CREATE_NOLOGIN_FAILED: 'FAILED',
};
export const CREATE_NOLOGIN_RERUN_STATUS_MAP: Record<
  CreateNoLoginRerunApiStatus,
  CreateProjectStatus
> = {
  CREATE_NOLOGIN_RERUN_TRIGGERED: 'SUBMITTED',
  CREATE_NOLOGIN_RERUN_INITIATED: 'SUBMITTED',
  CREATE_NOLOGIN_RERUN_INPROGRESS: 'PROCESSING',
  CREATE_NOLOGIN_RERUN_SUCCESSFUL: 'SUCCESSFUL',
  CREATE_NOLOGIN_RERUN_FAILED: 'FAILED',
};

export const GENERATE_WITHTESTPLAN_STATUS_MAP: Record<
  GenerateWithTestPlanApiStatus,
  GenerateProjectStatus
> = {
  GENERATE_WITHTESTPLAN_TRIGGERED: 'GENERATING',
  GENERATE_WITHTESTPLAN_INITIATED: 'GENERATING',
  GENERATE_WITHTESTPLAN_INPROGRESS: 'GENERATING',
  GENERATE_WITHTESTPLAN_SUCCESSFUL: 'GENERATE_SUCCESSFUL',
  GENERATE_WITHTESTPLAN_FAILED: 'GENERATE_FAILED',
  GENERATE_WITHTESTPLAN_STOP_TRIGGERED: 'GENERATE_STOPPING',
  GENERATE_WITHTESTPLAN_STOPPED_SUCCESSFUL: 'GENERATE_STOPPED',
};

export const GENERATE_WITHTESTPLAN_RERUN_STATUS_MAP: Record<
  GenerateWithTestPlanRerunApiStatus,
  GenerateProjectStatus
> = {
  GENERATE_WITHTESTPLAN_RERUN_TRIGGERED: 'GENERATING',
  GENERATE_WITHTESTPLAN_RERUN_INITIATED: 'GENERATING',
  GENERATE_WITHTESTPLAN_RERUN_INPROGRESS: 'GENERATING',
  GENERATE_WITHTESTPLAN_RERUN_SUCCESSFUL: 'GENERATE_SUCCESSFUL',
  GENERATE_WITHTESTPLAN_RERUN_FAILED: 'GENERATE_FAILED',
  GENERATE_WITHTESTPLAN_RERUN_STOP_TRIGGERED: 'GENERATE_STOPPING',
  GENERATE_WITHTESTPLAN_RERUN_STOPPED_SUCCESSFUL: 'GENERATE_STOPPED',
};

export const GENERATE_WITHOUTTESTPLAN_STATUS_MAP: Record<
  GenerateWithoutTestPlanApiStatus,
  GenerateProjectStatus
> = {
  GENERATE_WITHOUTTESTPLAN_TRIGGERED: 'GENERATING',
  GENERATE_WITHOUTTESTPLAN_INITIATED: 'GENERATING',
  GENERATE_WITHOUTTESTPLAN_INPROGRESS: 'GENERATING',
  GENERATE_WITHOUTTESTPLAN_SUCCESSFUL: 'GENERATE_SUCCESSFUL',
  GENERATE_WITHOUTTESTPLAN_FAILED: 'GENERATE_FAILED',
  GENERATE_WITHOUTTESTPLAN_STOP_TRIGGERED: 'GENERATE_STOPPING',
  GENERATE_WITHOUTTESTPLAN_STOPPED_SUCCESSFUL: 'GENERATE_STOPPED',
};

export const GENERATE_WITHOUTTESTPLAN_RERUN_STATUS_MAP: Record<
  GenerateWithoutTestPlanRerunApiStatus,
  GenerateProjectStatus
> = {
  GENERATE_WITHOUTTESTPLAN_RERUN_TRIGGERED: 'GENERATING',
  GENERATE_WITHOUTTESTPLAN_RERUN_INITIATED: 'GENERATING',
  GENERATE_WITHOUTTESTPLAN_RERUN_INPROGRESS: 'GENERATING',
  GENERATE_WITHOUTTESTPLAN_RERUN_SUCCESSFUL: 'GENERATE_SUCCESSFUL',
  GENERATE_WITHOUTTESTPLAN_RERUN_FAILED: 'GENERATE_FAILED',
  GENERATE_WITHOUTTESTPLAN_RERUN_STOP_TRIGGERED: 'GENERATE_STOPPING',
  GENERATE_WITHOUTTESTPLAN_RERUN_STOPPED_SUCCESSFUL: 'GENERATE_STOPPED',
};

export const EXECUTE_STATUS_MAP: Record<
  ExecuteApiStatus,
  ExecuteProjectStatus
> = {
  EXECUTE_TRIGGERED: 'EXECUTING',
  EXECUTE_INITIATED: 'EXECUTING',
  EXECUTE_INPROGRESS: 'EXECUTING',
  EXECUTE_SUCCESSFUL: 'EXECUTE_SUCCESSFUL',
  EXECUTE_FAILED: 'EXECUTE_FAILED',
  EXECUTE_STOP_TRIGGERED: 'EXECUTE_STOPPING',
  EXECUTE_STOPPED_SUCCESSFUL: 'EXECUTE_STOPPED',
};

export const EXECUTE_RERUN_STATUS_MAP: Record<
  ExecuteRerunApiStatus,
  ExecuteProjectStatus
> = {
  EXECUTE_RERUN_TRIGGERED: 'EXECUTING',
  EXECUTE_RERUN_INITIATED: 'EXECUTING',
  EXECUTE_RERUN_INPROGRESS: 'EXECUTING',
  EXECUTE_RERUN_SUCCESSFUL: 'EXECUTE_SUCCESSFUL',
  EXECUTE_RERUN_FAILED: 'EXECUTE_FAILED',
  EXECUTE_RERUN_STOP_TRIGGERED: 'EXECUTE_STOPPING',
  EXECUTE_RERUN_STOPPED_SUCCESSFUL: 'EXECUTE_STOPPED',
};

export const ENHANCE_STATUS_MAP: Record<
  EnhanceApiStatus,
  EnhanceProjectStatus
> = {
  ENHANCE_TRIGGERED: 'ENHANCING',
  ENHANCE_INITIATED: 'ENHANCING',
  ENHANCE_INPROGRESS: 'ENHANCING',
  ENHANCE_SUCCESSFUL: 'ENHANCE_SUCCESSFUL',
  ENHANCE_FAILED: 'ENHANCE_FAILED',
  ENHANCE_STOP_TRIGGERED: 'ENHANCE_STOPPING',
  ENHANCE_STOPPED_SUCCESSFUL: 'ENHANCE_STOPPED',
};

export const ENHANCE_RERUN_STATUS_MAP: Record<
  EnhanceRerunApiStatus,
  EnhanceProjectStatus
> = {
  ENHANCE_RERUN_TRIGGERED: 'ENHANCING',
  ENHANCE_RERUN_INITIATED: 'ENHANCING',
  ENHANCE_RERUN_INPROGRESS: 'ENHANCING',
  ENHANCE_RERUN_SUCCESSFUL: 'ENHANCE_SUCCESSFUL',
  ENHANCE_RERUN_FAILED: 'ENHANCE_FAILED',
  ENHANCE_RERUN_STOP_TRIGGERED: 'ENHANCE_STOPPING',
  ENHANCE_RERUN_STOPPED_SUCCESSFUL: 'ENHANCE_STOPPED',
};
// Transformation of API response project status to UI project status
export const IQE_PROJECT_STATUS_MAP: Record<IqeApiStatus, IqeProjectStatus> = {
  ...CREATE_LOGIN_STATUS_MAP,
  ...CREATE_LOGIN_RERUN_STATUS_MAP,
  ...CREATE_NOLOGIN_STATUS_MAP,
  ...CREATE_NOLOGIN_RERUN_STATUS_MAP,
  ...GENERATE_WITHTESTPLAN_STATUS_MAP,
  ...GENERATE_WITHTESTPLAN_RERUN_STATUS_MAP,
  ...GENERATE_WITHOUTTESTPLAN_STATUS_MAP,
  ...GENERATE_WITHOUTTESTPLAN_RERUN_STATUS_MAP,
  ...EXECUTE_STATUS_MAP,
  ...EXECUTE_RERUN_STATUS_MAP,
  ...ENHANCE_STATUS_MAP,
  ...ENHANCE_RERUN_STATUS_MAP,
};

export const IQE_PROJECT_CARD_STATUS_MESSAGE_MAP: Record<
  IqeProjectStatus,
  string
> = {
  SUBMITTED: 'Project creation request submitted. Please wait...',
  PROCESSING: 'Validating credentials and creating your project…',
  SUCCESSFUL: 'Project created successfully. Click the arrow to get started.',
  FAILED_LOGIN:
    'Unable to validate project. Please check the Non-Prod App URL and credentials.',
  FAILED:
    'Project creation failed due to an unexpected error. Please try again',

  GENERATING:
    'Generating and executing your test scenarios. This may take a few minutes.',
  GENERATE_SUCCESSFUL: 'Tests generated and executed successfully.',
  GENERATE_FAILED: 'Test generation failed. Please try again.',
  GENERATE_STOPPING: 'Stopping the run. Please wait...',
  GENERATE_STOPPED:
    'Run stopped. Test cases generated in this run were not saved. You can generate again anytime.',

  EXECUTING: 'Re-executing your test suite. Please wait...',
  EXECUTE_SUCCESSFUL: 'Test suite re-executed successfully.',
  EXECUTE_FAILED: 'Test execution failed. Please try again.',
  EXECUTE_STOPPING: 'Stopping the run. Please wait...',
  EXECUTE_STOPPED:
    'Run stopped before results were generated. You can run again to see results.',

  ENHANCING: 'Enhancing test coverage…',
  ENHANCE_SUCCESSFUL: 'Test enhancement completed',
  ENHANCE_FAILED: 'Test enhancement failed',
  ENHANCE_STOPPING: 'Stopping the run. Please wait...',
  ENHANCE_STOPPED:
    'Run stopped. No new test cases were added, and existing test cases are unchanged.',
};

export const DEFAULT_EMPTY_STATE_MESSAGE =
  'No test executions yet. Run your first test suite to generate scenarios, validate workflows, and receive AI-powered testing insights.';

// Copy shown on the full tab-section overlay while the trigger API is in flight, keyed by action.
export const TRIGGER_OVERLAY_TEXT: Record<
  TestExecutionActionKey,
  { primary: string; secondary: string }
> = {
  GENERATE: {
    primary: 'Preparing test generation...',
    secondary: 'Sending your request to the agent to generate your test cases',
  },
  RUN: {
    primary: 'Preparing test execution...',
    secondary: 'Sending your request to the agent to run your test cases',
  },
  ENHANCE: {
    primary: 'Preparing test enhancement...',
    secondary:
      'Sending your request to the agent to enhance your test coverage and re-run your tests.',
  },
};
